/**
 * Camera smoke detail panel (E.W.5). A PRE-FUSION camera-CV smoke verdict from the
 * E.W.2 camera_smoke_detections table — the camera-CV sibling of the satellite Raw
 * Hot Spots (operator-cop-doctrine §9.1).
 *
 * Deliberately has NO R.3 click-fire actions (Confirm Fire / Recommend Dispatch):
 * camera smoke is a pre-fusion SIGNAL, not an actionable incident. It feeds the F.1
 * fusion engine (the cameraSmoke corroborating signal), which produces the
 * actionable high_risk_hotspots. It links to the source camera and, when a
 * co-located fused hotspot exists (within 1 km), to that scored sibling.
 */
import { useEffect, useState } from 'react';
import type { CameraSmokeFeature, CameraSmokeDetail } from '@tsr/shared-types';
import { fetchCameraSmokeDetail } from './api';
import { CAMERA_SMOKE_COLOR, cameraSmokeConfidenceBand } from './styles/cameraSmoke';
import { logAction } from './lib/operatorActions';

function agoLabel(iso: string | null): string {
  if (!iso) return 'unknown';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** Normalized [x, y, w, h] → CSS percent box, when the bbox looks 0–1 normalized. */
function bboxOverlayStyle(bbox: number[] | null): React.CSSProperties | null {
  if (!bbox || bbox.length < 4) return null;
  const [x, y, w, h] = bbox as [number, number, number, number];
  if (![x, y, w, h].every((n) => Number.isFinite(n) && n >= 0 && n <= 1)) return null;
  return {
    position: 'absolute',
    left: `${x * 100}%`,
    top: `${y * 100}%`,
    width: `${w * 100}%`,
    height: `${h * 100}%`,
    border: `2px solid ${CAMERA_SMOKE_COLOR}`,
    boxShadow: '0 0 0 1px rgba(0,0,0,0.6)',
    pointerEvents: 'none',
  };
}

export function CameraSmokeBody({
  detection,
  onOpenFusedHotspot,
}: {
  detection: CameraSmokeFeature;
  /** Open the co-located F.1-scored hotspot (when one exists within 1 km). */
  onOpenFusedHotspot?: (id: string) => void;
}): JSX.Element {
  const p = detection.properties;
  const [lon, lat] = detection.geometry.coordinates;
  const [detail, setDetail] = useState<CameraSmokeDetail | null>(null);

  useEffect(() => {
    logAction('view_camera_smoke_detail', { detection_id: detection.id, camera_id: p.camera_id });
  }, [detection.id, p.camera_id]);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchCameraSmokeDetail(detection.id, ctl.signal)
      .then(setDetail)
      .catch(() => undefined);
    return () => ctl.abort();
  }, [detection.id]);

  const d = detail?.properties ?? null;
  const fused = d?.fused_hotspot_nearby ?? null;
  const bbox = d?.bbox ?? null;
  const overlay = bboxOverlayStyle(bbox);
  const thumb = d?.camera_snapshot_url ?? null;

  return (
    <div className="px-4 py-4 space-y-4" data-testid="camera-smoke-body">
      <div className="border-l-2 pl-3" style={{ borderColor: CAMERA_SMOKE_COLOR }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Camera smoke</p>
        <h2 className="text-base font-semibold">{p.camera_name}</h2>
        <p className="text-xs text-slate-400 mt-0.5">Detected {agoLabel(p.observed_at)}</p>
        <span className="inline-block mt-1.5 text-[10px] px-1.5 py-0.5 rounded font-medium bg-slate-700/60 text-slate-300">
          Pre-fusion · camera-CV detection
        </span>
      </div>

      {thumb && (
        <section>
          <div className="relative overflow-hidden rounded border border-slate-800 bg-slate-900">
            <img
              src={thumb}
              alt={`Latest frame from ${p.camera_name}`}
              className="w-full block"
              loading="lazy"
            />
            {overlay && <div style={overlay} aria-label="Detected smoke bounding box" />}
          </div>
          <p className="text-[10px] text-slate-500 mt-1">
            Latest camera frame{overlay ? ' · amber box = detected smoke region' : ''}.
          </p>
        </section>
      )}

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Detection</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Confidence</dt>
          <dd className="font-mono">
            {p.confidence != null ? `${Math.round(p.confidence)} / 100` : '—'} ·{' '}
            {cameraSmokeConfidenceBand(p.confidence)}
          </dd>
          <dt className="text-slate-400">Model</dt>
          <dd className="font-mono text-xs">{p.model}</dd>
          {d?.stage && (
            <>
              <dt className="text-slate-400">Stage</dt>
              <dd>{d.stage}</dd>
            </>
          )}
          {d?.ptz_azimuth_deg != null && (
            <>
              <dt className="text-slate-400">PTZ aim</dt>
              <dd className="font-mono">{Math.round(d.ptz_azimuth_deg)}°</dd>
            </>
          )}
          {p.frame_captured_at && (
            <>
              <dt className="text-slate-400">Frame captured</dt>
              <dd>{agoLabel(p.frame_captured_at)}</dd>
            </>
          )}
          <dt className="text-slate-400">Position</dt>
          <dd className="font-mono text-xs">
            {lat.toFixed(3)}, {lon.toFixed(3)}
          </dd>
        </dl>
      </section>

      {d?.reasoning && (
        <section className="border-t border-slate-800 pt-3">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
            Model reasoning
          </h3>
          <p className="text-[11px] text-slate-300 leading-relaxed">{d.reasoning}</p>
        </section>
      )}

      {d?.camera_viewer_url && (
        <section className="border-t border-slate-800 pt-3">
          <a
            href={d.camera_viewer_url}
            target="_blank"
            rel="noreferrer"
            className="block w-full text-center rounded bg-slate-700/70 hover:bg-slate-600 px-3 py-2 text-sm font-medium"
          >
            Open camera feed ↗
          </a>
        </section>
      )}

      {fused && (
        <section className="border-t border-slate-800 pt-3">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Fusion</h3>
          <p className="text-[11px] text-slate-400 mb-2 leading-relaxed">
            A co-located AI-fused hotspot exists ~{fused.distance_m} m away — this camera signal has
            a scored sibling.
          </p>
          <button
            type="button"
            onClick={() => onOpenFusedHotspot?.(fused.id)}
            className="w-full rounded bg-amber-600/80 hover:bg-amber-500 px-3 py-2 text-sm font-medium"
          >
            Open F.1 Fused Hotspot (conf {fused.confidence} · L{fused.response_level}) →
          </button>
        </section>
      )}

      <section className="border-t border-slate-800 pt-3">
        <p className="text-[11px] text-slate-400 leading-relaxed">
          Camera smoke is a <strong>pre-fusion</strong> signal: a camera-vision model saw smoke in
          this frame, but it has not been scored or corroborated. It is not an actionable incident —
          it feeds the F.1 fusion engine, which correlates it against other signals to produce the
          actionable high-risk hotspots in Urgent Alerts. No dispatch action is available here.
        </p>
      </section>

      <footer className="border-t border-slate-800 pt-3 text-[10px] text-slate-500 leading-relaxed space-y-0.5">
        <div>
          Camera: {p.camera_name}
          {p.camera_source ? ` (${p.camera_source})` : ''}
          {d?.camera_province ? ` · ${d.camera_province}` : ''}
        </div>
        {d?.prompt_version && <div>Prompt {d.prompt_version}</div>}
        <div>Claude-vision camera smoke-CV (E.W.2) — pre-fusion corroborating signal.</div>
      </footer>
    </div>
  );
}
